import { useFormBuilderStore } from '../model/form-builder.store';
import { useTableEditorStore } from '../model/table-editor.store';
import type { FormElement, TableProperties } from '../model/form.types';
import { useFormBuilderToast } from './useFormBuilderToast';
import {
  formBuilderCompactSuccessToastStyle,
  showFormElementDeleteSuccessToast,
} from './showFormElementDeleteToast';

function showDeleteSuccessToast(title = 'Elemento deletado com sucesso.'): void {
  const toast = useFormBuilderToast();
  toast.showSuccessToast({ title, type: 'success' }, formBuilderCompactSuccessToastStyle);
}

function removeElementWithUndo(
  element: FormElement,
  index: number,
  elementId: string,
  toastTitle?: string,
  withUndo = true
): void {
  const store = useFormBuilderStore();
  const snapshot = JSON.parse(JSON.stringify(element)) as FormElement;
  store.removeElement(elementId);

  const title = toastTitle || 'Elemento deletado com sucesso.';

  if (!withUndo) {
    showDeleteSuccessToast(title);
    return;
  }

  const toast = useFormBuilderToast();
  toast.showSuccessToast(
    {
      title,
      type: 'success',
      onUndo: () => {
        if (store.elements.some((el) => el.id === snapshot.id)) {
          return;
        }
        store.insertElementAt(index, JSON.parse(JSON.stringify(snapshot)) as FormElement);
        store.selectElement(snapshot.id);
      },
      undoLabel: 'Desfazer',
    },
    formBuilderCompactSuccessToastStyle
  );
}

export function removeFormBuilderBodyTable(elementId: string): boolean {
  const store = useFormBuilderStore();
  const index = store.elements.findIndex((el) => el.id === elementId);
  if (index === -1) {
    return false;
  }

  const element = store.elements[index];
  if (element.type !== 'TABLE') {
    return false;
  }
  if ((element.properties as TableProperties).isHeader === true) {
    return false;
  }

  removeElementWithUndo(element, index, elementId, 'Tabela deletada com sucesso', false);
  return true;
}

export function removeFormBuilderCanvasElement(elementId: string, withUndo = true): boolean {
  const store = useFormBuilderStore();
  const index = store.elements.findIndex((el) => el.id === elementId);
  if (index === -1) {
    return false;
  }

  const element = store.elements[index];
  if (element.type === 'HEADER') {
    return false;
  }
  if (element.type === 'TABLE' && (element.properties as TableProperties).isHeader === true) {
    return false;
  }
  if (element.type === 'TABLE') {
    return false;
  }

  removeElementWithUndo(element, index, elementId, undefined, withUndo);
  return true;
}

export function removeFormBuilderTableCellElements(withUndo = true): boolean {
  const tableEditorStore = useTableEditorStore();
  const tableId = tableEditorStore.activeTableId;

  if (!tableEditorStore.isEditingMode || !tableId) {
    return false;
  }

  const snapshots = tableEditorStore.selectedCellsData
    .filter((cell) => Boolean(cell.content))
    .map((cell) => ({
      rowIndex: cell.rowIndex,
      colIndex: cell.colIndex,
      content: JSON.parse(JSON.stringify(cell.content)) as FormElement,
    }));

  if (snapshots.length === 0) {
    return false;
  }

  tableEditorStore.clearSelectedCellsContent();
  tableEditorStore.clearCellSelection();

  if (withUndo) {
    showFormElementDeleteSuccessToast(() => {
      snapshots.forEach(({ rowIndex, colIndex, content }) => {
        tableEditorStore.setCellContent(
          tableId,
          rowIndex,
          colIndex,
          JSON.parse(JSON.stringify(content)) as FormElement
        );
      });
    });
  } else {
    showDeleteSuccessToast();
  }

  return true;
}

export function removeFormBuilderTableCellElement(withUndo = true): boolean {
  const tableEditorStore = useTableEditorStore();
  if (!tableEditorStore.isEditingMode || !tableEditorStore.selectedCell) {
    return false;
  }

  const cellData = tableEditorStore.selectedCellData;
  const tableId = tableEditorStore.activeTableId;

  if (!tableId || !cellData?.content) {
    return false;
  }

  const deletedContent = JSON.parse(JSON.stringify(cellData.content)) as FormElement;
  const { rowIndex, colIndex } = cellData;

  tableEditorStore.removeCellContent(rowIndex, colIndex);
  tableEditorStore.clearCellSelection();
  if (withUndo) {
    showFormElementDeleteSuccessToast(() => {
      tableEditorStore.setCellContent(
        tableId,
        rowIndex,
        colIndex,
        JSON.parse(JSON.stringify(deletedContent)) as FormElement
      );
    });
  } else {
    showDeleteSuccessToast();
  }

  return true;
}
